/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useNavigate, useParams } from "react-router-dom";
import formatDate, { formatDateWithTime } from "../../utilities/helper";
import { useFetchPickListShipmentById } from "../../apis/pickupApis";
import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronUp,
} from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import PickupShipmentLists from "./_components/PickupShipmentList";
import TimelineUI from "./_components/TimelineUI";

const Section = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-white shadow-sm border border-gray-200 rounded-2xl p-4 sm:p-5 space-y-4">
    {children}
  </div>
);

const TwoCol = ({ label, value }: { label: string; value: any }) => (
  <div className="flex flex-col gap-1">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-800">{value || ""}</p>
  </div>
);

const StatCard = ({
  label,
  value,
  color,
}: {
  label: string;
  value: any;
  color: string;
}) => (
  <div
    className={`rounded-xl p-3 text-white shadow-sm flex items-center space-x-2 ${color}`}
  >
    <p className="text-xs opacity-80">{label} :</p>
    <p className="text-lg font-bold">{value || 0}</p>
  </div>
);

const InfoTile = ({ label, value }: { label: string; value: any }) => (
  <div className="bg-gray-50 hover:bg-gray-100 transition rounded-xl p-3 border  flex items-center space-x-2">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-800 break-words">
      {value || ""}
    </p>
  </div>
);

const ManifestDetails = () => {
  const [openSection, setOpenSection] = useState<string | null>(
    "pickupRequestDetails",
  );
  const toggleSection = (section: string) => {
    setOpenSection((prev) => (prev === section ? null : section));
  };

  const navigate = useNavigate();
  const { id } = useParams<{ id: any }>();
  const pickupId = id;

  const [pageNo, setPageNo] = useState<number>(1);
  const [limit, setLimit] = useState<number>(20);
  const [allShipments, setAllShipments] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const {
    refetch,
    data: pickupData,
    isFetching,
    isLoading,
  } = useFetchPickListShipmentById(pageNo, limit, "", pickupId ?? "");

  const timeLine = (pickupData?.statusHistories || [])
    .sort(
      (a: any, b: any) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )
    .map((item: any, index: number) => ({
      id: item.id || index + 1,
      label: item.newStatus
        ?.toLowerCase()
        ?.replace(/_/g, " ")
        ?.replace(/\b\w/g, (char: string) => char.toUpperCase()),
      date: formatDateWithTime(item.createdAt),
      details: item.details || "",
    }));

  useEffect(() => {
    if (pickupData && !isLoading && !isFetching) {
      const newShipments = pickupData?.generatedShipments ?? [];

      if (pageNo === 1) {
        setAllShipments(newShipments);
      } else {
        setAllShipments((prev) => {
          const newIds = new Set(newShipments.map((item: any) => item.id));
          const filtered = prev.filter((item: any) => !newIds.has(item.id));
          return [...filtered, ...newShipments];
        });
      }

      setHasMore(newShipments.length >= limit);
      setIsLoadingMore(false);
    }
  }, [pickupData, isLoading, isFetching, pageNo, limit]);

  useEffect(() => {
    refetch();
  }, [pageNo, limit]);

  const statusStyles = {
    GENERATED: "bg-yellow-100 text-yellow-800 border-yellow-300",
    ASSIGNED_TO_DRIVER: "bg-blue-100 text-blue-800 border-blue-300",
    COMPLETED: "bg-green-100 text-green-800 border-green-300",
    PENDING: "bg-red-100 text-red-800 border-red-300",
    COLLECTED: "bg-gray-100 text-gray-700 border-gray-300",
    ASSIGNED_TO_STAFF: "bg-cyan-100 text-cyan-800 border-cyan-300",
    RECEIVE_INBOUND: "bg-gray-100 text-gray-700 border-gray-300",
  } as const;

  const formattedStatus = pickupData?.status
    ? pickupData?.status
        ?.toLowerCase()
        ?.replace(/_/g, " ")
        ?.replace(/\b\w/g, (char: string) => char.toUpperCase())
    : "";

  return (
    <div className="p-3 sm:p-5 space-y-5 ">
      {/* HEADER */}
      <div className="flex flex-wrap gap-3 justify-between items-center bg-white p-3 sm:p-4 rounded-xl shadow-sm border">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 border rounded-full hover:bg-gray-100 transition"
          >
            <IconChevronLeft size={20} />
          </button>

          <h1 className="text-base sm:text-lg font-semibold text-gray-800">
            Request ID: {pickupData?.pickupId}
          </h1>
        </div>

        <span
          className={`px-4 py-1 text-xs rounded-full border font-medium ${
            statusStyles[pickupData?.status as keyof typeof statusStyles] ||
            "bg-gray-100"
          }`}
        >
          {formattedStatus}
        </span>
      </div>

      {/* DETAILS */}
      <TimelineUI steps={timeLine} currentStep={timeLine.length - 1} />
      <Section>
        {/* Header */}
        <div
          onClick={() => toggleSection("pickupRequestDetails")}
          className="flex justify-between items-center cursor-pointer"
        >
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            📦 Pickup Request Details
          </h2>

          <button className="p-2 border rounded-full hover:bg-gray-100 transition">
            {openSection === "pickupRequestDetails" ? (
              <IconChevronUp size={22} stroke={1.5} />
            ) : (
              <IconChevronDown size={22} stroke={1.5} />
            )}
          </button>
        </div>

        {openSection === "pickupRequestDetails" && (
          <div className="bg-white border rounded-2xl p-5 shadow-sm hover:shadow-md transition">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                🚚 Pickup Details
              </h3>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <InfoTile
                label="Sender Name"
                value={pickupData?.clientName || pickupData?.outletName}
              />
              <InfoTile
                label="Packages"
                value={pickupData?.totalShipmentsCount}
              />
              <InfoTile
                label="Pickup Date"
                value={formatDate(pickupData?.pickupDate)}
              />
              <InfoTile label="Time Slot" value={pickupData?.timeSlot} />

              {pickupData?.driverCode && (
                <>
                  <InfoTile
                    label="Driver Name"
                    value={pickupData?.driverName}
                  />
                  <InfoTile
                    label="Driver Code"
                    value={pickupData?.driverCode}
                  />
                </>
              )}

              {pickupData?.staffName && (
                <InfoTile label="Staff Name" value={pickupData?.staffName} />
              )}
              {pickupData?.warehouses && (
                <>
                  <InfoTile label="Warehouse" value={pickupData?.warehouses} />
                  <InfoTile label="Received By" value={pickupData?.staffName} />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 xl:col-span-3">
                    <StatCard
                      label="Total"
                      value={pickupData?.totalShipmentsCount}
                      color="bg-gray-800"
                    />
                    <StatCard
                      label="Received"
                      value={pickupData?.verifiedShipmentsCount}
                      color="bg-green-500"
                    />
                    <StatCard
                      label="Missing"
                      value={pickupData?.missingShipmentsCount}
                      color="bg-yellow-500"
                    />
                    <StatCard
                      label="Damage"
                      value={pickupData?.damageShipmentsCount}
                      color="bg-red-500"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </Section>

      {/* <Section> 
        <div
          onClick={() => toggleSection("pickupRequestDetails")}
          className="flex justify-between items-center cursor-pointer"
        >
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            📦 Pickup Request Details
          </h2>

          <button className="p-2 border rounded-full hover:bg-gray-100 transition">
            {openSection === "pickupRequestDetails" ? (
              <IconChevronUp size={22} stroke={1.5} color="#6B7280" />
            ) : (
              <IconChevronDown size={22} stroke={1.5} color="#6B7280" />
            )}
          </button>
        </div>
 
        {openSection === "pickupRequestDetails" && (
          <div className=" grid grid-cols-1 md:grid-cols-1 xl:grid-cols-2 gap-4"> 
            <div className="bg-white border rounded-2xl p-5 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                🚚 Pickup Details
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <TwoCol
                  label="Sender Name"
                  value={pickupData?.clientName || pickupData?.outletName}
                />
                <TwoCol
                  label="Packages"
                  value={pickupData?.totalShipmentsCount}
                />
                <TwoCol
                  label="Pickup Date"
                  value={formatDate(pickupData?.pickupDate)}
                />
                <TwoCol label="Time Slot" value={pickupData?.timeSlot} />

                {pickupData?.driverCode && (
                  <>
                    <TwoCol
                      label="Driver Name"
                      value={pickupData?.driverName}
                    />
                    <TwoCol
                      label="Driver Code"
                      value={pickupData?.driverCode}
                    />
                  </>
                )}

                {pickupData?.staffName && (
                  <TwoCol label="Staff Name" value={pickupData?.staffName} />
                )}
              </div>
            </div>
 
            {pickupData?.warehouses && (
              <div className="bg-white border rounded-2xl p-5 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  🏭 RIW Details
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <TwoCol label="Warehouse" value={pickupData?.warehouses} />
                  <TwoCol label="Received By" value={pickupData?.staffName} />
                  <TwoCol
                    label="Total"
                    value={pickupData?.totalShipmentsCount}
                  />
                  <TwoCol
                    label="Received"
                    value={pickupData?.verifiedShipmentsCount}
                  />
                  <TwoCol
                    label="Missing"
                    value={pickupData?.missingShipmentsCount}
                  />
                  <TwoCol
                    label="Damage"
                    value={pickupData?.damageShipmentsCount}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </Section> */}

      {/* <Section>
        <div
          onClick={() => toggleSection("pickupRequestDetails")}
          className="flex justify-between items-center cursor-pointer"
        >
          <h2 className="text-lg font-semibold mb-4 text-gray-800">
            Pickup Request Details
          </h2>
          <button className="p-2 border rounded-full hover:bg-gray-100 transition">
            {openSection === "pickupRequestDetails" ? (
              <IconChevronUp size={22} stroke={1.5} color="#6B7280" />
            ) : (
              <IconChevronDown size={22} stroke={1.5} color="#6B7280" />
            )}
          </button>
        </div>
        {openSection === "pickupRequestDetails" && (
          <div className="grid grid-cols-1 md:grid-cols-1 xl:grid-cols-1 gap-4">
            <div className="bg-gray-50 border rounded-xl overflow-hidden flex items-center ">
              <h3 className="px-4 py-2 bg-gray-100 font-semibold text-sm">
                Pickup Details
              </h3>
              <TwoCol
                label="Sender Name"
                value={pickupData?.clientName || pickupData?.outletName}
              />

              <TwoCol
                label="Packages"
                value={pickupData?.totalShipmentsCount}
              />
              <TwoCol
                label="Pickup Date"
                value={formatDate(pickupData?.pickupDate)}
              />
              <TwoCol label="Time Slot" value={pickupData?.timeSlot} />
              {pickupData?.driverCode && (
                <>
                  <TwoCol label="Driver Name" value={pickupData?.driverName} />
                  <TwoCol label="Driver Code" value={pickupData?.driverCode} />
                </>
              )}
              {pickupData?.staffName && (
                <TwoCol label="Staff Name" value={pickupData?.staffName} />
              )}
            </div>

            {pickupData?.warehouses && (
              <div className="bg-gray-50 border rounded-xl overflow-hidden flex items-center">
                <h3 className="px-4 py-2 bg-gray-100 font-semibold text-sm">
                  RIW Details
                </h3>
                <TwoCol label="Warehouse" value={pickupData?.warehouses} />
                <TwoCol label="Received By" value={pickupData?.staffName} />
                <TwoCol label="Total" value={pickupData?.totalShipmentsCount} />
                <TwoCol
                  label="Received"
                  value={pickupData?.verifiedShipmentsCount}
                />
                <TwoCol
                  label="Missing"
                  value={pickupData?.missingShipmentsCount}
                />
                <TwoCol
                  label="Damage"
                  value={pickupData?.damageShipmentsCount}
                />
              </div>
            )}
          </div>
        )}
      </Section> */}

      {/* SHIPMENT LIST */}
      <div className="bg-white rounded-xl border shadow-sm p-3 sm:p-4">
        <div
          onClick={() => toggleSection("pickupShipmentList")}
          className="flex justify-between items-center cursor-pointer"
        >
          <h2 className="text-lg font-semibold mb-4 text-gray-800">
            Shipments List
          </h2>
          <button className="p-2 border rounded-full hover:bg-gray-100 transition">
            {openSection === "pickupShipmentList" ? (
              <IconChevronUp size={22} stroke={1.5} color="#6B7280" />
            ) : (
              <IconChevronDown size={22} stroke={1.5} color="#6B7280" />
            )}
          </button>
        </div>
        {openSection === "pickupShipmentList" && (
          <PickupShipmentLists
            pickupData={pickupData}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            pageNo={pageNo}
          />
        )}
      </div>
    </div>
  );
};

export default ManifestDetails;
